
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Menu,
  X,
  Search,
  Phone,
  Mail,
  ChevronDown,
  ExternalLink,
  Youtube,
} from "lucide-react";

import { NAV_LINKS } from "../mock";
import { Link } from "react-router-dom";
import API_BASE_URL from "../api/api";

import NoticeHeader from "../components/NoticeHeader";

// const API_BASE_URL =
//   window.location.hostname === "localhost" ||
//     window.location.hostname === "127.0.0.1"
//     ? "http://127.0.0.1:8000/api"
//     : "https://api.kuntl.net/api";

const FACULTIES_API = `${API_BASE_URL}/faculties`;

const DEPARTMENTS_API = `${API_BASE_URL}/departments`;

const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");


export default function Header() {
  const [open, setOpen] = useState(false);

  /*
   * ============================================================
   * DESKTOP DROPDOWN STATES
   * ============================================================
   */

  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubMenu, setActiveSubMenu] =
    useState(null);



  useEffect(() => {
    const fetchFacultyAndDepartments = async () => {
      try {
        setLoadingFaculties(true);

        const [facultyResponse, departmentResponse] =
          await Promise.all([
            fetch(FACULTIES_API),
            fetch(DEPARTMENTS_API),
          ]);

        const facultyResult = await facultyResponse.json();
        const departmentResult = await departmentResponse.json();

        // console.log("FACULTIES:", facultyResult);
        // console.log("DEPARTMENTS:", departmentResult);

        if (
          !facultyResult.status ||
          !Array.isArray(facultyResult.data)
        ) {
          throw new Error("Invalid faculties response");
        }

        if (
          !departmentResult.status ||
          !Array.isArray(departmentResult.data)
        ) {
          throw new Error("Invalid departments response");
        }

        const faculties = facultyResult.data.filter(
          (faculty) => Number(faculty.status) === 1
        );

        const departments = departmentResult.data.filter(
          (department) => Number(department.status) === 1
        );

        const dynamicFaculties = faculties.map((faculty) => {
          const facultyDepartments = departments
            .filter(
              (department) =>
                Number(department.faculty_id) ===
                Number(faculty.id)
            )
            .map((department) => ({
              label: department.name,

              url: `/faculty/${slugify(
                faculty.name
              )}/${slugify(department.name)}`,

              departmentId: department.id,
              facultyId: faculty.id,
            }));

          return {
            label: faculty.name,
            facultyId: faculty.id,
            code: faculty.code,
            children: facultyDepartments,
          };
        });

        // console.log(
        //   "DYNAMIC FACULTIES:",
        //   dynamicFaculties
        // );

        setNavLinks((previousLinks) =>
          previousLinks.map((menu) =>
            menu.title === "Faculties"
              ? {
                ...menu,
                items: dynamicFaculties,
              }
              : menu
          )
        );
      } catch (error) {
        console.error(
          "Faculty/Department API Error:",
          error
        );
      } finally {
        setLoadingFaculties(false);
      }
    };

    fetchFacultyAndDepartments();
  }, []);

  /*
   * ============================================================
   * MOBILE MENU STATES
   * ============================================================
   */

  const [mobileActiveMenu, setMobileActiveMenu] =
    useState(null);

  const [
    mobileActiveSubMenu,
    setMobileActiveSubMenu,
  ] = useState(null);

  /*
   * ============================================================
   * SEARCH STATES
   * ============================================================
   */

  const [searchOpen, setSearchOpen] =
    useState(false);

  const [searchQuery, setSearchQuery] =
    useState("");

  /*
   * ============================================================
   * NAVIGATION
   * ============================================================
   */

  const [navLinks, setNavLinks] =
    useState(NAV_LINKS);

  const [loadingFaculties, setLoadingFaculties] =
    useState(true);

  /*
   * ============================================================
   * DROPDOWN POSITION
   * ============================================================
   */

  const [subMenuPosition, setSubMenuPosition] =
    useState({
      top: 0,
      left: 0,
    });

  /*
   * ============================================================
   * DESKTOP NAVIGATION REF
   * ============================================================
   *
   * Used to detect click outside navigation/dropdowns.
   */

  const desktopNavRef = useRef(null);

  /*
   * ============================================================
   * FETCH FACULTIES + DEPARTMENTS
   * ============================================================
   */

  useEffect(() => {
    const fetchFacultyAndDepartments = async () => {
      try {
        setLoadingFaculties(true);

        const [
          facultyResponse,
          departmentResponse,
        ] = await Promise.all([
          fetch(FACULTIES_API),
          fetch(DEPARTMENTS_API),
        ]);

        if (!facultyResponse.ok) {
          throw new Error(
            `Faculty API error: ${facultyResponse.status}`
          );
        }

        if (!departmentResponse.ok) {
          throw new Error(
            `Department API error: ${departmentResponse.status}`
          );
        }

        const facultyResult =
          await facultyResponse.json();

        const departmentResult =
          await departmentResponse.json();

    

        if (
          !facultyResult.status ||
          !Array.isArray(facultyResult.data)
        ) {
          throw new Error(
            "Invalid faculties API response"
          );
        }

        if (
          !departmentResult.status ||
          !Array.isArray(departmentResult.data)
        ) {
          throw new Error(
            "Invalid departments API response"
          );
        }

        /*
         * Only active faculties
         */

        const activeFaculties =
          facultyResult.data.filter(
            (faculty) =>
              Number(faculty.status) === 1
          );

        /*
         * Only active departments
         */

        const activeDepartments =
          departmentResult.data.filter(
            (department) =>
              Number(department.status) === 1
          );

        /*
         * Create dynamic faculties
         */

        const dynamicFaculties =
          activeFaculties.map((faculty) => {
            const facultyDepartments =
              activeDepartments
                .filter(
                  (department) =>
                    Number(
                      department.faculty_id
                    ) === Number(faculty.id)
                )
                .map((department) => ({
                  label: department.name,

                  url: `/faculty/${slugify(
                    faculty.name
                  )}/${slugify(
                    department.name
                  )}`,

                  departmentId:
                    department.id,

                  facultyId: faculty.id,
                }));

            return {
              label: faculty.name,

              facultyId: faculty.id,

              code: faculty.code,

              children: facultyDepartments,
            };
          });

        // console.log(
        //   "DYNAMIC FACULTIES:",
        //   dynamicFaculties
        // );

        /*
         * Replace only Faculties menu.
         */

        setNavLinks((previousLinks) =>
          previousLinks.map((menu) => {
            if (
              menu.title === "Faculties"
            ) {
              return {
                ...menu,
                items: dynamicFaculties,
              };
            }

            return menu;
          })
        );
      } catch (error) {
        console.error(
          "Failed to fetch faculties/departments:",
          error
        );
      } finally {
        setLoadingFaculties(false);
      }
    };

    fetchFacultyAndDepartments();
  }, []);

  /*
   * ============================================================
   * CLICK OUTSIDE DESKTOP DROPDOWN
   * ============================================================
   *
   * IMPORTANT:
   *
   * There is NO timer.
   * There is NO mouseLeave closing.
   *
   * Dropdown closes ONLY when clicking outside
   * the complete desktop navigation area.
   */

  useEffect(() => {
    const handleOutsideClick = (event) => {
      /*
       * If there is no active menu,
       * nothing needs to happen.
       */

      if (!activeMenu) {
        return;
      }

      /*
       * Check whether click happened inside
       * navigation/dropdown area.
       */

      if (
        desktopNavRef.current &&
        desktopNavRef.current.contains(
          event.target
        )
      ) {
        return;
      }

      /*
       * Click happened outside.
       * Close everything.
       */

      setActiveMenu(null);
      setActiveSubMenu(null);
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, [activeMenu]);

  /*
   * ============================================================
   * SEARCH
   * ============================================================
   */

  const searchResults = useMemo(() => {
    const query = searchQuery
      .trim()
      .toLowerCase();

    if (!query) {
      return [];
    }

    const results = [];

    navLinks.forEach((menu) => {
      /*
       * Main menu title
       */

      if (
        menu.title &&
        menu.title
          .toLowerCase()
          .includes(query)
      ) {
        results.push({
          label: menu.title,
          url: menu.url || "#",
        });
      }

      if (!Array.isArray(menu.items)) {
        return;
      }

      menu.items.forEach((item) => {
        /*
         * String item
         */

        if (typeof item === "string") {
          if (
            item
              .toLowerCase()
              .includes(query)
          ) {
            results.push({
              label: item,
              url: "#",
            });
          }

          return;
        }

        /*
         * Faculty / parent item
         */

        if (
          item.label &&
          item.label
            .toLowerCase()
            .includes(query)
        ) {
          results.push({
            label: item.label,
            url: item.url || "#",
          });
        }

        /*
         * Department child
         */

        if (
          Array.isArray(item.children)
        ) {
          item.children.forEach(
            (child) => {
              if (
                child.label &&
                child.label
                  .toLowerCase()
                  .includes(query)
              ) {
                results.push({
                  label: child.label,
                  url:
                    child.url || "#",
                });
              }
            }
          );
        }
      });
    });

    return results;
  }, [navLinks, searchQuery]);

  /*
   * ============================================================
   * DESKTOP MENU CLICK
   * ============================================================
   */

  const handleMenuClick = (
    menu,
    event
  ) => {
    const hasDropdown =
      Array.isArray(menu.items) &&
      menu.items.length > 0;

    /*
     * Normal menu without dropdown
     */

    if (!hasDropdown) {
      setActiveMenu(null);
      setActiveSubMenu(null);

      return;
    }

    /*
     * Same menu clicked again
     *
     * Close it.
     */

    if (
      activeMenu === menu.title
    ) {
      setActiveMenu(null);
      setActiveSubMenu(null);

      return;
    }

    /*
     * Open clicked menu.
     */

    setActiveMenu(menu.title);

    setActiveSubMenu(null);

    /*
     * Calculate dropdown position.
     */

    if (event?.currentTarget) {
      const rect =
        event.currentTarget.getBoundingClientRect();

      setSubMenuPosition({
        top: rect.bottom,
        left: rect.left,
      });
    }
  };

  /*
   * ============================================================
   * DESKTOP SUB MENU
   * ============================================================
   */

  const handleSubMenuEnter = (
    item
  ) => {
    if (
      Array.isArray(item.children) &&
      item.children.length > 0
    ) {
      setActiveSubMenu(item.label);
    }
  };

  /*
   * ============================================================
   * MOBILE MENU
   * ============================================================
   */

  const closeMobileMenu = () => {
    setOpen(false);

    setMobileActiveMenu(null);

    setMobileActiveSubMenu(null);
  };

  /*
   * ============================================================
   * SEARCH OPEN
   * ============================================================
   */

  const openSearch = () => {
    setSearchOpen(true);

    setSearchQuery("");

    /*
     * Close desktop dropdown when
     * search opens.
     */

    setActiveMenu(null);

    setActiveSubMenu(null);
  };

  /*
   * ============================================================
   * RETURN
   * ============================================================
   */
  // button A++ start
  const [fontScale, setFontScale] = useState(1);

const increaseFont = () => {
  setFontScale((prev) => Math.min(prev + 0.1, 1.3));
};

const decreaseFont = () => {
  setFontScale((prev) => Math.max(prev - 0.1, 0.8));
};

const resetFont = () => {
  setFontScale(1);
};
// button A++ end
// apply dropdown button start


const [isAdmissionOpen, setIsAdmissionOpen] = useState(false);

const admissionLinks = [
  // {
  //   label: "UG Admissions",
  //   url: "https://ukadmission.samarth.ac.in/",
  // },
  // {
  //   label: "PG Admissions",
  //   url: "https://ukpgadmission.samarth.ac.in/index.php/site/",
  // },
  {
    label: "KU ERP Login-Student",
    url: "https://kuntl.net/students/login",
  },
  {
    label: "KU ERP Login-College",
    url: "https://kuntl.net/college/College",
  },
  {
    label: "KU Samarth Login - Student",
    url: "https://kunainital.samarth.edu.in/index.php/site/login",
  },
  {
    label: "KU Samarth Login - College",
    url: "https://kunainital.samarth.ac.in/index.php/site/login",
  },
];

// apply dropdown button end

  return (
    <header className="sticky top-0 z-50 bg-white">
    

    {/* svg border start */}
     {/* <div className="bg-[rgb(226,102,108)] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between gap-2 min-h-[38px] text-xs">
            
          </div>
        </div>
      </div> */}
 
    {/* svg border end */}
      {/* ======================================================
          TOP STRIP
      ======================================================= */}

      {/* <div className="bg-[#B5121B] text-white"> */}
<div className="bg-gradient-to-r from-[var(--terracotta-deep)] via-[var(--terracotta)] to-[var(--saffron)] text-white">
  <div className="max-w-7xl mx-auto px-4">

    <div
      className="
        min-h-[38px]
        py-2
        flex flex-col
        md:flex-row
        md:flex-wrap
        lg:flex-nowrap
        items-center
        justify-between
        gap-2
        lg:gap-4
        text-xs
      "
    >

      {/* ================= LEFT : PHONE + EMAIL ================= */}
      <div
        className="
          flex
          flex-wrap
          items-center
          justify-center
          md:justify-start
          gap-3
          lg:gap-4
          w-full
          md:w-auto
        "
      >

        <a
          href="tel:05942235563"
          className="flex items-center gap-1.5 hover:opacity-80 whitespace-nowrap"
        >
          <Phone size={13} />
          <span>05942-235563</span>
        </a>

        <a
          href="mailto:registrar@kunainital.ac.in"
          className="flex items-center gap-1.5 hover:opacity-80 whitespace-nowrap"
        >
          <Mail size={13} />
          <span>registrar@kunainital.ac.in</span>
        </a>

      </div>


      {/* ================= RIGHT LINKS ================= */}
      <div
        className="
          flex
          flex-wrap
          items-center
          justify-center
          md:justify-end
          gap-x-4
          gap-y-2
          w-full
          md:w-auto
        "
      >

        <a
          href="/nirf"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 whitespace-nowrap"
        >
          NIRF
        </a>
        <a
          href="https://nad.gov.in/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 whitespace-nowrap"
        >
          NAD
        </a>
        <a
          href="/cell-club/Internal-Quality-Assurance-Cell-(IQAC)"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 whitespace-nowrap"
        >
          IQAC
        </a>

        <a
          href="/events/recruitments"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 whitespace-nowrap"
        >
          Recruitments
        </a>

        <a
          href="https://hrdcnainital.ac.in/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 whitespace-nowrap"
        >
          UGC-MMTTC
        </a>

        <a
          href="/sdcnep"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 whitespace-nowrap"
        >
          SDC NEP 2020
        </a>


        {/* ================= FONT BUTTONS ================= */}
        <div
          className="
            flex
            items-center
            gap-1
            ml-0
            md:ml-1
          "
        >

          <button
            type="button"
            onClick={decreaseFont}
            aria-label="Decrease font size"
            className="
              h-7 w-7
              sm:h-8 sm:w-8
              rounded-full
              bg-white
              text-[#444]
              font-bold
              text-xs sm:text-sm
              hover:bg-gray-200
              transition
            "
          >
            A-
          </button>

          <button
            type="button"
            onClick={resetFont}
            aria-label="Reset font size"
            className="
              h-7 w-7
              sm:h-8 sm:w-8
              rounded-full
              bg-white
              text-[#444]
              font-bold
              text-xs sm:text-sm
              hover:bg-gray-200
              transition
            "
          >
            A
          </button>

          <button
            type="button"
            onClick={increaseFont}
            aria-label="Increase font size"
            className="
              h-7 w-7
              sm:h-8 sm:w-8
              rounded-full
              bg-white
              text-[#444]
              font-bold
              text-xs sm:text-sm
              hover:bg-gray-200
              transition
            "
          >
            A+
          </button>
    {[Youtube].map((I, i) => (
  <a
    key={i}
    href="https://youtube.com/watch?v=RCJ-qyWRUbI&feature=shared"
    target="_blank"
    rel="noopener noreferrer"
   className="h-9 w-9 grid place-items-center rounded-full bg-white border border-white hover:bg-gray-300 hover:border-gray-300"
  >
    <I size={20} className="text-red-600" />
  </a>
))}

        </div>
 
      </div>

    </div>
  </div>
</div>
<div className="h-1 bg-white"></div>
<NoticeHeader />
      {/* ======================================================
          LOGO SECTION
      ======================================================= */}



      {/* <div className="bg-[var(--paper)] border-b border-[var(--cream-deep)] shadow-[0_2px_16px_rgba(184,69,44,0.06)]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-20 py-3 md:py-0 flex items-center justify-center">
        
          <a href="/" className="flex items-center justify-center">
            <div
              className="
          flex flex-col md:flex-row
          items-center justify-center
          gap-2 md:gap-0
        "
            >
              
              <h1
                className="
            text-[#B5121B]
            text-[28px] md:text-[40px]
            font-extrabold
            whitespace-nowrap
            leading-none
            mr-0 md:mr-2
            text-center
          "
                style={{
                  fontFamily: "'Lobster', cursive",
                  WebkitTextStroke: "1px #B5121B",
                  paintOrder: "stroke fill",
                }}
              >
                Kumaun University
              </h1>

             
             <div
              className="
                h-[75px] md:h-[90px]
                w-[78px]
                overflow-hidden
                relative
                shrink-0
              "
            >
              <img
                src="/images/Kumaun_University_logo.png"
                alt="Kumaun University Logo"
                className="absolute h-[70px] md:h-[68px] w-[800px] max-w-none object-contain"
                style={{
                  left: "-360px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </div>

             
              <h1
                className="
            text-[#B5121B]
            text-[26px] md:text-[36px]
            font-extrabold
            whitespace-nowrap
            leading-none
            ml-0 md:ml-2
            text-center
          "
                style={{
                  fontFamily: "'Lobster', cursive",
                  WebkitTextStroke: "1px #B5121B",
                  paintOrder: "stroke fill",
                }}
              >
                कुमाऊँ विश्वविद्यालय
              </h1>
            </div>
          </a>
        </div>
      </div> */}
  


<div
  style={{ zoom: fontScale }}
  className="bg-[var(--paper)] border-b border-[var(--cream-deep)] shadow-[0_2px_16px_rgba(184,69,44,0.06)] relative"
>
  <div className="w-full bg-white border-t-2 border-[#e9eff7]">
    <div className="w-full px-2 sm:px-3 lg:px-4 py-2">

      <div className="grid grid-cols-12 items-center gap-1 sm:gap-2">

        {/* LEFT: UNIVERSITY LOGO + NAMES */}
        <a
          href="/"
          className="col-span-12 sm:col-span-5 flex items-center gap-2 sm:gap-1 min-w-0"
        >
          {/* UNIVERSITY LOGO */}
          <img
            src="/images/Kumaun_University_logo.png"
            alt="Kumaun University Logo"
            className="w-[62px] h-[62px] min-w-[62px] sm:w-[55px] sm:h-[55px] sm:min-w-[55px] md:w-[65px] md:h-[65px] lg:w-[80px] lg:h-[80px] object-contain shrink-0"
          />

          {/* UNIVERSITY NAMES */}
          <div className="flex flex-col justify-center min-w-0 flex-1">

            <div className="flex items-center gap-1 sm:gap-1">

              {/* HINDI NAME */}
              <div className="flex-1 min-w-0 text-right">
                <h1
                  className="text-[#B5121B] text-[clamp(12px,3.5vw,18px)] sm:text-[clamp(10px,1.55vw,27px)] font-semibold leading-tight m-0 whitespace-nowrap"
                  style={{
                    fontFamily: "'Bodoni Moda', serif",
                    letterSpacing: "-0.3px",
                  }}
                >
                  कुमाऊँ विश्वविद्यालय
                </h1>

                <p
                  className="text-[#B5121B] text-[clamp(9px,2.7vw,14px)] sm:text-[clamp(8px,0.95vw,17px)] font-semibold leading-tight m-0 whitespace-nowrap"
                  style={{
                    fontFamily: "'Bodoni Moda', serif",
                    letterSpacing: "-0.2px",
                  }}
                >
                  नैनीताल, उत्तराखंड
                </p>
              </div>

              {/* VERTICAL DIVIDER */}
              <div className="h-[32px] sm:h-[40px] lg:h-[48px] w-px bg-[#B5121B] shrink-0" />

              {/* ENGLISH NAME */}
              <div className="flex-1 min-w-0">
                <h1
                  className="text-[#B5121B] text-[clamp(10px,3.1vw,16px)] sm:text-[clamp(10px,1.55vw,27px)] font-semibold leading-tight m-0 whitespace-nowrap"
                  style={{
                    fontFamily: "'Bodoni Moda', serif",
                    letterSpacing: "-0.4px",
                  }}
                >
                  Kumaun University
                </h1>

                <p
                  className="text-[#B5121B] text-[clamp(8px,2.3vw,12px)] sm:text-[clamp(8px,0.95vw,17px)] font-semibold leading-tight m-0 whitespace-nowrap"
                  style={{
                    fontFamily: "'Bodoni Moda', serif",
                    letterSpacing: "-0.2px",
                  }}
                >
                  Nainital, Uttarakhand
                </p>
              </div>

            </div>

            {/* AISHE CODE */}
            <p className="text-[#B5121B] text-[8px] sm:text-[7px] lg:text-[8px] text-center leading-tight mt-1 mb-0 whitespace-nowrap">
              State University (KU AISHE CODE - U-0562)
            </p>

          </div>
        </a>

        {/* RIGHT: GOVERNMENT + MINISTRY + UGC LOGOS
            HIDDEN ON MOBILE */}
        <div className="hidden sm:grid sm:col-span-7 grid-cols-3 items-center justify-items-center gap-1 md:gap-2 lg:gap-4 min-w-0">

          {/* UTTARAKHAND GOVERNMENT */}
          <div className="flex items-center justify-center w-full min-w-0">
            <img
              src="/images/uk.webp"
              alt="Uttarakhand Government"
              className="w-full max-w-[130px] h-[38px] sm:h-[55px] md:h-[70px] lg:h-[85px] object-contain"
            />
          </div>

          {/* MINISTRY OF EDUCATION */}
          <div className="flex items-center justify-center w-full min-w-0">
            <img
              src="/images/saty_mev_jayte.webp"
              alt="Ministry of Education, Government of India"
              className="w-full max-w-[150px] h-[38px] sm:h-[55px] md:h-[70px] lg:h-[85px] object-contain"
            />
          </div>

          {/* UGC LOGO */}
          <div className="flex items-center justify-center w-full min-w-0">
            <img
              src="/images/ugc_logo.png"
              alt="University Grants Commission"
              className="w-full max-w-[180px] h-[45px] sm:h-[55px] md:max-w-[220px] md:h-[80px] lg:max-w-[240px] lg:h-[85px] object-contain"
            />
          </div>

        </div>

      </div>
    </div>
  </div>
</div>





{/* header end kumaun logo section */}

      <div style={{ zoom: fontScale }} className="md:hidden bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3 md:py-4">

            {/* Mobile Actions */}
            <div className="md:hidden w-full flex items-center justify-between">

              {/* Left: Search + Menu */}
              <div className="flex items-center gap-2">
                {/* <button
                  type="button"
                  onClick={openSearch}
                  className="w-10 h-10 flex items-center justify-center rounded-md text-gray-700"
                  aria-label="Search"
                >
                  <Search size={21} />
                </button> */}

                <button
                  type="button"
                  onClick={() => setOpen(!open)}
                  className="w-10 h-10 flex items-center justify-center rounded-md text-[#B5121B]"
                  aria-label="Menu"
                >
                  {open ? <X size={25} /> : <Menu size={25} />}
                </button>
              </div>

              {/* Right: Apply Now */}
              {/* <a
                href="https://ukadmission.samarth.ac.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#B5121B] text-white text-sm font-semibold hover:opacity-90 transition"
              >
                Apply Now
                <ExternalLink size={14} />
              </a> */}
                <button
                  type="button"
                  onClick={openSearch}
                  className="w-10 h-10 flex items-center justify-center rounded-md text-gray-700"
                  aria-label="Search"
                >
                  <Search size={21} />
                </button>
              <div className="relative inline-block">
              
              <button
                type="button"
                onClick={() => setIsAdmissionOpen((prev) => !prev)}
                aria-expanded={isAdmissionOpen}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#B5121B] text-white text-sm font-semibold hover:bg-[#94101A] transition"
              >
                Apply Now
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${
                    isAdmissionOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

                {isAdmissionOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 shadow-lg rounded-sm overflow-hidden"
                    style={{ zIndex: 9999 }}
                  >
                    {admissionLinks.map((link) => (
                      <a
                        key={link.label}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsAdmissionOpen(false)}
                        className="block px-4 py-3 text-sm font-medium text-gray-800 border-b border-gray-100 last:border-b-0 hover:bg-gray-100 hover:text-[#B5121B] transition"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>



      {/* ======================================================
          DESKTOP NAVIGATION
      ======================================================= */}

      <nav
        ref={desktopNavRef}
         style={{ zoom: fontScale }} className="hidden md:block bg-white border-b border-gray-200 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center">
            {navLinks.map((menu) => {
              const hasDropdown =
                Array.isArray(menu.items) &&
                menu.items.length > 0;

              const isActive =
                activeMenu ===
                menu.title;

              return (
                <div
                  key={menu.title}
                  className="relative"
                >
                  {/* ==================================================
                      MAIN NAV ITEM
                  =================================================== */}

                  {menu.url &&
                    !hasDropdown ? (
                    <Link
                      to={menu.url}
                      onClick={() => {
                        setActiveMenu(
                          null
                        );

                        setActiveSubMenu(
                          null
                        );
                      }}
                      className="flex items-center gap-1 px-4 py-3 text-sm font-semibold text-gray-800 hover:text-[#B5121B] transition whitespace-nowrap"
                    >
                      {menu.title}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={(event) =>
                        handleMenuClick(
                          menu,
                          event
                        )
                      }
                      className="flex items-center gap-1 px-4 py-3 text-sm font-semibold text-gray-800 hover:text-[#B5121B] transition whitespace-nowrap"
                    >
                      {menu.title}

                      {hasDropdown && (
                        <ChevronDown
                          size={15}
                          className={`transition-transform duration-200 ${isActive
                              ? "rotate-180"
                              : ""
                            }`}
                        />
                      )}
                    </button>
                  )}

                  {/* ==================================================
                      MAIN DROPDOWN
                  =================================================== */}

                  {hasDropdown &&
                    activeMenu ===
                    menu.title && (
                      <div className="fixed z-[100] bg-white border border-gray-200 shadow-xl min-w-[260px] max-w-[320px] max-h-[400px] overflow-y-auto overflow-x-hidden"
                        style={{
                          top: `${subMenuPosition.top}px`,
                          left: `${subMenuPosition.left}px`,
                        }}
                        /*
                         * IMPORTANT:
                         *
                         * NO onMouseLeave here.
                         *
                         * It will stay open until
                         * user clicks outside.
                         */
                        onMouseEnter={() => {
                          setActiveMenu(
                            menu.title
                          );
                        }}
                      >
                        {menu.items.map(
                          (
                            item,
                            index
                          ) => {
                            const isObject =
                              typeof item ===
                              "object" &&
                              item !== null;

                            const label =
                              isObject
                                ? item.label
                                : item;

                            const children =
                              isObject
                                ? item.children
                                : null;

                            const hasChildren =
                              Array.isArray(
                                children
                              ) &&
                              children.length >
                              0;

                            const isSubActive =
                              activeSubMenu ===
                              label;

                            return (
                              <div
                                key={`${label}-${index}`}
                                className="relative border-b border-gray-100 last:border-b-0"
                                onMouseEnter={() => {
                                  handleSubMenuEnter(
                                    item
                                  );
                                }}
                              >
                                {/* ==================================================
                                    FACULTY / MENU ITEM
                                =================================================== */}

                                {hasChildren ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveSubMenu(
                                        isSubActive
                                          ? null
                                          : label
                                      );
                                    }}
                                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 hover:text-[#B5121B] transition ${isSubActive
                                        ? "bg-gray-50 text-[#B5121B]"
                                        : ""
                                      }`}
                                  >
                                    <span>
                                      {label}
                                    </span>

                                    <ChevronDown
                                      size={15}
                                      className="-rotate-90 flex-shrink-0"
                                    />
                                  </button>
                                ) : (
                                  <Link
                                    to={
                                      isObject &&
                                        item.url
                                        ? item.url
                                        : "#"
                                    }
                                    className="block px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 hover:text-[#B5121B] transition"
                                    onClick={() => {
                                      setActiveMenu(
                                        null
                                      );

                                      setActiveSubMenu(
                                        null
                                      );
                                    }}
                                  >
                                    {label}
                                  </Link>
                                )}

                                {/* ==================================================
                                    DEPARTMENT SUB DROPDOWN
                                =================================================== */}

                                {hasChildren &&
                                  activeSubMenu ===
                                  label && (
                                    // <div
                                      // className="fixed z-[110] bg-white border border-gray-200 shadow-xl min-w-[280px] max-w-[340px] max-h-[400px] overflow-y-auto"
                                      <div className="fixed z-[110] bg-white border border-gray-200 shadow-xl min-w-[280px] max-w-[340px] max-h-[350px] overflow-y-auto overflow-x-hidden"
                                      style={{
                                        top: `${Math.min(
                                          window.innerHeight -
                                          420,
                                          subMenuPosition.top
                                        )}px`,

                                        left: `${subMenuPosition.left +
                                          260
                                          }px`,
                                      }}
                                      /*
                                       * IMPORTANT:
                                       *
                                       * NO onMouseLeave.
                                       *
                                       * Sub-dropdown remains
                                       * open until outside click.
                                       */
                                      onMouseEnter={() => {
                                        setActiveMenu(
                                          menu.title
                                        );

                                        setActiveSubMenu(
                                          label
                                        );
                                      }}
                                    >
                                      {children.map(
                                        (
                                          child,
                                          childIndex
                                        ) => (
                                          <Link
                                            key={`${child.label}-${childIndex}`}
                                            to={
                                              child.url ||
                                              "#"
                                            }
                                            className="block px-4 py-3 text-sm text-gray-800 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 hover:text-[#B5121B] transition"
                                            onClick={() => {
                                              setActiveMenu(
                                                null
                                              );

                                              setActiveSubMenu(
                                                null
                                              );
                                            }}
                                          >
                                            {
                                              child.label
                                            }
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                </div>
              );
            })}

            {/* ==================================================
                DESKTOP SEARCH
            =================================================== */}

            <div className="ml-auto flex items-center">
              <button
                type="button"
                onClick={openSearch}
                className="p-3 text-gray-700 hover:text-[#B5121B] transition"
                aria-label="Search"
              >
                <Search size={20} />
              </button>
            </div>

            {/* <a
              href="https://ukadmission.samarth.ac.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#B5121B] text-white text-sm font-semibold hover:opacity-90 transition"
            >
              Apply Now11
              <ExternalLink size={14} />
            </a> */}
            
          <div className="relative inline-block">
            <button
              type="button"
              onClick={() => setIsAdmissionOpen((prev) => !prev)}
              aria-expanded={isAdmissionOpen}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#B5121B] text-white text-sm font-semibold hover:bg-[#94101A] transition"
            >
              Apply Now
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${
                  isAdmissionOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isAdmissionOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 shadow-lg rounded-sm overflow-hidden"
                style={{ zIndex: 9999 }}
              >
                {admissionLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsAdmissionOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-gray-800 border-b border-gray-100 last:border-b-0 hover:bg-gray-100 hover:text-[#B5121B] transition"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          </div>
        </div>
      </nav>

      {/* ======================================================
          MOBILE MENU
      ======================================================= */}

      {open && (
        <div className="md:hidden bg-white border-b border-gray-200 shadow-lg max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="px-4 py-3">
            {navLinks.map((menu) => {
              const hasDropdown =
                Array.isArray(menu.items) &&
                menu.items.length > 0;

              const isMobileOpen =
                mobileActiveMenu ===
                menu.title;

              return (
                <div
                  key={menu.title}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  {hasDropdown ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileActiveMenu(
                            isMobileOpen
                              ? null
                              : menu.title
                          );

                          setMobileActiveSubMenu(
                            null
                          );
                        }}
                        className="w-full flex items-center justify-between py-3 text-left text-sm font-semibold text-gray-800"
                      >
                        <span>
                          {menu.title}
                        </span>

                        <ChevronDown
                          size={17}
                          className={`transition-transform ${isMobileOpen
                              ? "rotate-180"
                              : ""
                            }`}
                        />
                      </button>

                      {isMobileOpen && (
                        <div className="pb-2 pl-3">
                          {menu.items.map(
                            (
                              item,
                              index
                            ) => {
                              const isObject =
                                typeof item ===
                                "object" &&
                                item !== null;

                              const label =
                                isObject
                                  ? item.label
                                  : item;

                              const children =
                                isObject
                                  ? item.children
                                  : null;

                              const hasChildren =
                                Array.isArray(
                                  children
                                ) &&
                                children.length >
                                0;

                              const mobileSubOpen =
                                mobileActiveSubMenu ===
                                label;

                              return (
                                <div
                                  key={`${label}-${index}`}
                                  className="border-l border-gray-200"
                                >
                                  {hasChildren ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setMobileActiveSubMenu(
                                            mobileSubOpen
                                              ? null
                                              : label
                                          )
                                        }
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:text-[#B5121B]"
                                      >
                                        <span>
                                          {
                                            label
                                          }
                                        </span>

                                        <ChevronDown
                                          size={
                                            15
                                          }
                                          className={`transition-transform ${mobileSubOpen
                                              ? "rotate-180"
                                              : ""
                                            }`}
                                        />
                                      </button>

                                      {mobileSubOpen && (
                                        <div className="pl-4 pb-2">
                                          {children.map(
                                            (
                                              child,
                                              childIndex
                                            ) => (
                                              <Link
                                                key={`${child.label}-${childIndex}`}
                                                to={
                                                  child.url ||
                                                  "#"
                                                }
                                                onClick={
                                                  closeMobileMenu
                                                }
                                                className="block py-2 px-3 text-sm text-gray-600 hover:text-[#B5121B]"
                                              >
                                                {
                                                  child.label
                                                }
                                              </Link>
                                            )
                                          )}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <Link
                                      to={
                                        isObject &&
                                          item.url
                                          ? item.url
                                          : "#"
                                      }
                                      onClick={
                                        closeMobileMenu
                                      }
                                      className="block px-3 py-2.5 text-sm text-gray-700 hover:text-[#B5121B]"
                                    >
                                      {label}
                                    </Link>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={menu.url || "#"}
                      onClick={closeMobileMenu}
                      className="block py-3 text-sm font-semibold text-gray-800 hover:text-[#B5121B]"
                    >
                      {menu.title}
                    </Link>
                  )}
                </div>
              );
            })}

            {/* Mobile Apply button */}

            <div className="pt-4 pb-2">
              {/* <a
                href="https://ukadmission.samarth.ac.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-md bg-[#B5121B] text-white text-sm font-semibold"
              >
                Apply Now

                <ExternalLink size={15} />
              </a> */}
              {/* <div className="relative inline-block">
  <button
    type="button"
    onClick={() => setIsAdmissionOpen((prev) => !prev)}
    aria-expanded={isAdmissionOpen}
    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#B5121B] text-white text-sm font-semibold hover:bg-[#94101A] transition"
  >
    Apply Now
    <ChevronDown
      size={16}
      className={`transition-transform duration-200 ${
        isAdmissionOpen ? "rotate-180" : ""
      }`}
    />
  </button>

  {isAdmissionOpen && (
    <div
      className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 shadow-lg rounded-sm overflow-hidden"
      style={{ zIndex: 9999 }}
    >
      {admissionLinks.map((link) => (
        <a
          key={link.label}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setIsAdmissionOpen(false)}
          className="block px-4 py-3 text-sm font-medium text-gray-800 border-b border-gray-100 last:border-b-0 hover:bg-gray-100 hover:text-[#B5121B] transition"
        >
          {link.label}
        </a>
      ))}
    </div>
  )}
              </div> */}
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          SEARCH OVERLAY
      ======================================================= */}

      {searchOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/50"
          onClick={(event) => {
            /*
             * Close search only when clicking
             * the dark background.
             */

            if (
              event.target ===
              event.currentTarget
            ) {
              setSearchOpen(false);
              setSearchQuery("");
            }
          }}
        >
          <div className="bg-white w-full max-w-3xl mx-auto mt-20 rounded-lg shadow-2xl overflow-hidden">
            {/* Search header */}

            <div className="flex items-center gap-3 p-4 border-b border-gray-200">
              <Search
                size={21}
                className="text-gray-500 flex-shrink-0"
              />

              <input
                type="text"
                value={searchQuery}
                onChange={(event) =>
                  setSearchQuery(
                    event.target.value
                  )
                }
                placeholder="Search..."
                autoFocus
                className="flex-1 outline-none text-base text-gray-800"
              />

              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQuery("");
                }}
                className="p-1 text-gray-500 hover:text-[#B5121B]"
                aria-label="Close search"
              >
                <X size={22} />
              </button>
            </div>

            {/* Search results */}

            <div className="max-h-[60vh] overflow-y-auto">
              {searchQuery.trim() ===
                "" ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  Search Kumaun University
                </div>
              ) : searchResults.length ===
                0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No results found.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {searchResults.map(
                    (
                      result,
                      index
                    ) => (
                      <Link
                        key={`${result.label}-${index}`}
                        to={
                          result.url || "#"
                        }
                        onClick={() => {
                          setSearchOpen(
                            false
                          );

                          setSearchQuery("");
                        }}
                        className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-gray-50 transition"
                      >
                        <span className="text-sm text-gray-800">
                          {result.label}
                        </span>

                        <ExternalLink
                          size={15}
                          className="text-[#B5121B] flex-shrink-0"
                        />
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

 
    </header>
  );
}

